import React, { useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Container,
  Paper,
  Step,
  StepButton,
  StepLabel,
  StepIcon,
  Stepper,
  Typography,
  useMediaQuery,
  alpha,
} from '@mui/material';
import { keyframes } from '@mui/system';
import { styled, useTheme } from '@mui/material/styles';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { Link as RouterLink } from 'react-router-dom';

// Shake animation for error fields
const shakeAnimation = keyframes`
  0%, 100% { transform: translateX(0); }
  10%, 30%, 50%, 70%, 90% { transform: translateX(-5px); }
  20%, 40%, 60%, 80% { transform: translateX(5px); }
`;

// Global styles for form animations
const GlobalStyles = styled('style')({
  '@global': {
    '.shake-error': {
      animation: `${shakeAnimation} 0.6s ease-in-out`
    }
  }
});

// Styled components for enhanced visual experience
const StyledPaper = styled(Paper)(({ theme, magical }) => {
  const colors = theme.palette;
  return {
    padding: theme.spacing(3),
    marginBottom: theme.spacing(3),
    borderRadius: magical ? 16 : 8,
    boxShadow: magical
      ? theme.palette.mode === 'dark'
        ? '0 6px 16px rgba(0, 0, 0, 0.08), 0 3px 6px rgba(0, 0, 0, 0.04), 0 1px 3px rgba(123, 104, 238, 0.12)'
        : `0 6px 16px rgba(0, 0, 0, 0.08), 0 3px 6px rgba(0, 0, 0, 0.04), 0 1px 3px ${alpha(colors.primary.main, 0.12)}`
      : theme.shadows[1],
    overflow: 'hidden',
  };
});

const StepperContainer = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(3),
}));

const StepContentContainer = styled(Box)(({ theme, active }) => ({
  padding: theme.spacing(2, 0, 3),
  minHeight: 200,
  position: 'relative',
  opacity: active ? 1 : 0,
  transform: active ? 'translateY(0)' : 'translateY(10px)',
  transition: 'opacity 0.3s ease-in-out, transform 0.3s ease-out',
}));

const StepNavigationContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  marginTop: theme.spacing(4),
}));

// Custom StepIcon component to match the original design
const CustomStepIcon = ({ icon, active, completed, error }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 32,
        height: 32,
        borderRadius: '50%',
        backgroundColor: error
          ? theme.palette.error.main
          : completed
            ? theme.palette.success.main
            : active
              ? isDark ? theme.palette.secondary.main : theme.palette.primary.main
              : isDark ? alpha(theme.palette.text.disabled, 0.2) : '#E0E0E0',
        color: completed || active || error
          ? theme.palette.common.white
          : isDark ? theme.palette.text.disabled : theme.palette.text.primary,
        transition: 'all 0.3s ease',
        border: !completed && !active && !error ? `1px solid ${isDark ? 'transparent' : '#BDBDBD'}` : 'none',
        ...(error && {
          boxShadow: `0 0 0 4px ${alpha(theme.palette.error.main, 0.2)}`,
        }),
        ...(active && {
          boxShadow: `0 0 0 4px ${alpha(isDark ? theme.palette.secondary.main : theme.palette.primary.main, 0.15)}`,
        }),
        '& > *': { // Style for the icon inside
          color: 'inherit',
          fontSize: '1rem',
        },
      }}
    >
      {error ? '!' : icon}
    </Box>
  );
};

/**
 * FormStepper - Standardized multi-step form component
 * 
 * @param {Object} props - Component properties
 * @param {Array} props.steps - Array of step objects with label, icon, and component properties
 * @param {number} props.activeStep - Current active step (optional, controlled mode)
 * @param {Function} props.onStepChange - Callback when step changes (optional, controlled mode)
 * @param {Function} props.onSubmit - Callback when form is submitted
 * @param {Function} props.onCancel - Callback when form is cancelled
 * @param {Function} props.validateStep - Function to validate current step before proceeding
 * @param {boolean} props.linear - Whether steps must be completed in order
 * @param {boolean} props.showCompletionStep - Whether to show a completion step
 * @param {React.ReactNode} props.completionContent - Content to show on completion step
 * @param {string} props.nextButtonText - Text for the next button (default: "Next")
 * @param {string} props.backButtonText - Text for the back button (default: "Back")
 * @param {string} props.submitButtonText - Text for the submit button (default: "Submit")
 * @param {string} props.cancelLink - Link to navigate to when cancel button is clicked
 * @param {boolean} props.submitting - Whether the form is currently submitting
 * @param {boolean} props.magical - Whether to apply magical styling
 * @param {React.ReactNode} props.submitButtonIcon - Icon for the submit button
 * @param {Object} props.sx - Additional styles for the container
 */
const FormStepper = ({
  steps = [],
  activeStep: controlledActiveStep,
  onStepChange,
  onSubmit,
  onCancel,
  validateStep,
  linear = true,
  showCompletionStep = false,
  completionContent,
  nextButtonText = "Next",
  backButtonText = "Back",
  submitButtonText = "Submit",
  cancelLink,
  submitting = false,
  magical = false,
  submitButtonIcon,
  sx = {},
  ...props
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints?.down('sm') || '(max-width: 600px)');
  
  // For uncontrolled mode
  const [internalActiveStep, setInternalActiveStep] = useState(0);
  const [completed, setCompleted] = useState({});
  const [validationError, setValidationError] = useState(false);
  
  // Use controlled or uncontrolled activeStep
  const activeStep = controlledActiveStep !== undefined ? controlledActiveStep : internalActiveStep;
  
  // Calculate total steps
  const totalSteps = steps.length;
  const isLastStep = activeStep === totalSteps - 1;
  const allStepsCompleted = Object.keys(completed).length === totalSteps;
  
  // Mark step as completed
  const handleComplete = () => {
    const newCompleted = { ...completed };
    newCompleted[activeStep] = true;
    setCompleted(newCompleted);
  };

  // Handle step click
  const handleStep = (step) => () => {
    // In linear mode, prevent skipping ahead
    if (linear && step > activeStep && !completed[activeStep - 1]) {
      return;
    }
    
    if (onStepChange) {
      onStepChange(step);
    } else {
      setInternalActiveStep(step);
    }
    
    // Clear any validation errors when changing steps
    setValidationError(false);
  };

  // Handle next button click
  const handleNext = async () => {
    // Set validation error state to false initially
    setValidationError(false);
    
    // Always touch all fields in the current step first
    // This ensures we show validation errors even on first attempt
    if (steps[activeStep]?.touchFields) {
      await steps[activeStep].touchFields();
    }

    // Short delay to ensure the touched state is applied before validation
    await new Promise(resolve => setTimeout(resolve, 50));

    // Validate current step if validation function provided
    if (validateStep) {
      try {
        const isValid = await validateStep(activeStep);

        if (!isValid) {
          setValidationError(true);

          // Trigger the shake animation on invalid fields by adding/removing error class
          // This creates a visual cue for the user
          const errorFields = document.querySelectorAll('.Mui-error');
          errorFields.forEach(field => {
            field.classList.add('shake-error');
            setTimeout(() => field.classList.remove('shake-error'), 600);
          });
          
          // Find the first error field and scroll to it
          if (errorFields.length > 0) {
            errorFields[0].scrollIntoView({ 
              behavior: 'smooth', 
              block: 'center' 
            });
          }

          return;
        }
      } catch (error) {
        console.error("Step validation error:", error);
        setValidationError(true);
        return;
      }
    }

    // Reset validation error state
    setValidationError(false);

    if (isLastStep) {
      if (onSubmit) {
        onSubmit();
      }
      if (showCompletionStep) {
        // Move to completion step
        if (onStepChange) {
          onStepChange(totalSteps);
        } else {
          setInternalActiveStep(totalSteps);
        }
      }
    } else {
      // Mark current step complete
      handleComplete();
      
      // Move to next step
      const newActiveStep = activeStep + 1;
      if (onStepChange) {
        onStepChange(newActiveStep);
      } else {
        setInternalActiveStep((prevStep) => prevStep + 1);
      }
    }
  };

  // Handle back button click
  const handleBack = () => {
    if (onStepChange) {
      onStepChange(activeStep - 1);
    } else {
      setInternalActiveStep((prevStep) => prevStep - 1);
    }
    
    // Clear any validation errors when going back
    setValidationError(false);
  };

  // Reset the stepper
  const handleReset = () => {
    if (onStepChange) {
      onStepChange(0);
    } else {
      setInternalActiveStep(0);
      setCompleted({});
    }
    
    setValidationError(false);
  };

  return (
    <Container maxWidth="lg" disableGutters sx={sx} {...props}>
      <GlobalStyles />
      <StyledPaper magical={magical}>
        <StepperContainer>
          <Stepper 
            activeStep={activeStep} 
            alternativeLabel={isMobile}
            nonLinear={!linear}
          >
            {steps.map((step, index) => (
              <Step key={step.label} completed={completed[index]}>
                {linear ? (
                  <StepLabel
                    error={validationError && activeStep === index}
                    StepIconComponent={(props) => <CustomStepIcon {...props} error={validationError && activeStep === index} />}
                    icon={step.icon ? React.cloneElement(step.icon, { fontSize: 'small' }) : undefined}
                    optional={step.optional ? <Typography variant="caption">Optional</Typography> : null}
                    sx={{
                      '& .MuiStepLabel-label': {
                        color: validationError && activeStep === index ? 'error.main' : 'inherit',
                        ...(validationError && activeStep === index && {
                          fontWeight: 500
                        })
                      }
                    }}
                  >
                    {step.label}
                  </StepLabel>
                ) : (
                  <StepButton
                    onClick={handleStep(index)}
                    icon={
                      <CustomStepIcon
                        icon={step.icon ? React.cloneElement(step.icon, { fontSize: 'small' }) : index + 1}
                        error={validationError && activeStep === index}
                      />
                    }
                    sx={{
                      '& .MuiStepLabel-label': {
                        color: validationError && activeStep === index ? 'error.main' : 'inherit',
                        ...(validationError && activeStep === index && {
                          fontWeight: 500
                        })
                      }
                    }}
                  >
                    {step.label}
                  </StepButton>
                )}
              </Step>
            ))}
            {showCompletionStep && (
              <Step completed={allStepsCompleted}>
                <StepLabel icon={<CheckCircleIcon color="success" />}>
                  Complete
                </StepLabel>
              </Step>
            )}
          </Stepper>
        </StepperContainer>

        {activeStep === totalSteps && showCompletionStep ? (
          // Completion Step
          <Box>
            {completionContent || (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <CheckCircleIcon color="success" sx={{ fontSize: 60, mb: 2 }} />
                <Typography variant="h5" gutterBottom>
                  All steps completed!
                </Typography>
                <Typography variant="body1" color="text.secondary" paragraph>
                  You have successfully completed all the required steps.
                </Typography>
                <Button onClick={handleReset} variant="outlined" sx={{ mt: 2 }}>
                  Reset
                </Button>
              </Box>
            )}
          </Box>
        ) : (
          // Step Content with Progress Indicator
          <Box>
            {/* Progress indicator */}
            <Box sx={{
              width: '100%',
              height: 4,
              backgroundColor: (theme) => alpha(theme.palette.divider, 0.2),
              borderRadius: 2,
              mb: 3,
              overflow: 'hidden',
              position: 'relative'
            }}>
              <Box sx={{
                height: '100%',
                width: `${(activeStep / (totalSteps - 1)) * 100}%`,
                backgroundImage: (theme) => theme.palette.mode === 'dark'
                  ? `linear-gradient(90deg,
                      ${alpha(theme.palette.primary.main, 0.8)} 0%,
                      ${alpha(theme.palette.secondary.main, 0.9)} 100%)`
                  : `linear-gradient(90deg,
                      ${theme.palette.primary.main} 0%,
                      ${theme.palette.secondary.main} 100%)`,
                borderRadius: 2,
                transition: 'width 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)'
              }} />
            </Box>

            <StepContentContainer active={true}>
              {validationError && (
                <Box
                  sx={{
                    mb: 3,
                    p: 2,
                    borderRadius: 1,
                    backgroundColor: (theme) => alpha(theme.palette.error.main, theme.palette.mode === 'dark' ? 0.1 : 0.05),
                    borderLeft: (theme) => `4px solid ${theme.palette.error.main}`,
                  }}
                >
                  <Typography
                    color="error"
                    variant="body2"
                    sx={{ fontWeight: 500, display: 'flex', alignItems: 'center' }}
                  >
                    <Box
                      component="span"
                      sx={{
                        mr: 1,
                        display: 'inline-flex',
                        '& svg': {
                          fontSize: 20,
                        }
                      }}
                    >
                      ⚠️
                    </Box>
                    Please correct the errors in this step before proceeding.
                  </Typography>
                </Box>
              )}

              {/* Step Summary - provides context for current step */}
              <Box sx={{ mb: 3 }}>
                <Typography
                  variant="h6"
                  sx={{
                    color: (theme) => alpha(theme.palette.text.primary, 0.9),
                    mb: 1,
                    fontWeight: 600
                  }}
                >
                  {steps[activeStep]?.label}
                </Typography>
                {steps[activeStep]?.description && (
                  <Typography variant="body2" color="text.secondary">
                    {steps[activeStep]?.description}
                  </Typography>
                )}
              </Box>

              {steps[activeStep]?.component}
            </StepContentContainer>
            
            <StepNavigationContainer>
              {activeStep === 0 && cancelLink ? (
                <Button
                  component={RouterLink}
                  to={cancelLink}
                  startIcon={<ChevronLeftIcon sx={{ fontSize: '1.3rem' }} />}
                  variant="outlined"
                  sx={{
                    color: theme.palette.mode === 'dark' ? undefined : 'primary.main',
                    borderColor: theme.palette.mode === 'dark' ? undefined : 'primary.main',
                    '& .MuiSvgIcon-root': {
                      color: theme.palette.mode === 'dark' ? 'inherit' : theme.palette.primary.main,
                      opacity: 1,
                      fontSize: '1.3rem'
                    }
                  }}
                >
                  {backButtonText}
                </Button>
              ) : (
                <Button
                  disabled={activeStep === 0 || submitting}
                  onClick={handleBack}
                  startIcon={<ChevronLeftIcon sx={{ fontSize: '1.3rem' }} />}
                  variant="outlined"
                  sx={{
                    color: theme.palette.mode === 'dark' ? undefined : 'primary.main',
                    borderColor: theme.palette.mode === 'dark' ? undefined : 'primary.main',
                    '& .MuiSvgIcon-root': {
                      color: theme.palette.mode === 'dark' ? 'inherit' : theme.palette.primary.main,
                      opacity: 1,
                      fontSize: '1.3rem'
                    }
                  }}
                >
                  {backButtonText}
                </Button>
              )}
              
              <Box>
                {onCancel && !cancelLink && (
                  <Button 
                    onClick={onCancel} 
                    sx={{ mr: 1 }}
                    variant="text"
                    disabled={submitting}
                  >
                    Cancel
                  </Button>
                )}
                
                {isLastStep ? (
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleNext}
                    endIcon={submitting ?
                      <CircularProgress size={20} color="inherit" /> :
                      submitButtonIcon ? React.cloneElement(submitButtonIcon, { sx: { color: '#fff', fontSize: '1.3rem' } }) : null
                    }
                    disabled={submitting}
                    sx={magical ? {
                      boxShadow: theme.palette.mode === 'dark'
                        ? '0 4px 10px rgba(123, 104, 238, 0.25)'
                        : `0 4px 10px ${alpha(theme.palette.primary.main, 0.25)}`,
                      background: theme.palette.primary.main,
                      '&:hover': {
                        boxShadow: theme.palette.mode === 'dark'
                          ? '0 6px 16px rgba(123, 104, 238, 0.4)'
                          : `0 6px 16px ${alpha(theme.palette.primary.main, 0.4)}`,
                        background: theme.palette.primary.dark,
                      }
                    } : {}}
                  >
                    {submitButtonText}
                  </Button>
                ) : (
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleNext}
                    endIcon={<ChevronRightIcon sx={{ color: '#fff', fontSize: '1.3rem' }} />}
                    disabled={submitting}
                    sx={{
                      color: '#fff',
                      boxShadow: theme.palette.mode === 'dark'
                        ? '0 4px 10px rgba(123, 104, 238, 0.25)'
                        : `0 4px 10px ${alpha(theme.palette.primary.main, 0.25)}`,
                      '&:hover': {
                        boxShadow: theme.palette.mode === 'dark'
                          ? '0 6px 16px rgba(123, 104, 238, 0.4)'
                          : `0 6px 16px ${alpha(theme.palette.primary.main, 0.4)}`,
                      }
                    }}
                  >
                    {nextButtonText}
                  </Button>
                )}
              </Box>
            </StepNavigationContainer>
          </Box>
        )}
      </StyledPaper>
    </Container>
  );
};

export default FormStepper;